import { Head, Link } from '@inertiajs/react'
import { HiOutlineEnvelope, HiOutlinePencil, HiOutlinePhone } from 'react-icons/hi2'
import AppLayout from '../components/AppLayout'
import { avatarSwatch } from '../lib/avatarColor'

type UserRole = 'manager' | 'sales'

type UserCard = {
  id: number
  name: string
  email: string
  phone: string | null
  role: UserRole
  initials: string
  photo_url: string | null
}

type Props = {
  users: UserCard[]
}

const ROLE_LABELS: Record<UserRole, string> = {
  manager: 'Manager',
  sales: 'Sales',
}

export default function Users({ users }: Props) {
  return (
    <>
      <Head title="User Management" />
      <AppLayout active="users">
        <div className="flex items-end justify-between gap-4">
          <h1 className="font-display text-3xl font-bold tracking-tight text-ink md:text-[40px] md:leading-[1.2]">
            User Management
          </h1>
          <Link
            href="/users/new"
            className="inline-flex items-center justify-center rounded-md bg-ink px-8 py-4 font-display text-base font-medium text-white shadow-sm transition hover:bg-midnight-green"
          >
            Create User
          </Link>
        </div>

        <div className="mt-6">
          <h2 className="font-display text-base font-medium text-black/60">
            Users ({users.length})
          </h2>

          <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {users.map((user) => (
              <UserCardItem key={user.id} user={user} />
            ))}
          </div>
        </div>
      </AppLayout>
    </>
  )
}

function UserCardItem({ user }: { user: UserCard }) {
  return (
    <div className="flex flex-col gap-4 rounded-xl bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-4">
          <Avatar user={user} />
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h3 className="truncate font-display text-lg font-medium text-ink">
              {user.name}
            </h3>
            <span className="inline-flex w-fit items-center rounded-full bg-[#93D500]/20 px-3 py-0.5 font-display text-xs font-medium text-midnight-green">
              {ROLE_LABELS[user.role]}
            </span>
          </div>
        </div>
        <Link
          href={`/users/${user.id}/edit`}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50"
          aria-label={`Edit ${user.name}`}
        >
          <HiOutlinePencil className="h-5 w-5" />
        </Link>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-2 font-display text-sm text-ink">
          <HiOutlineEnvelope className="h-5 w-5 text-gray-500" aria-hidden="true" />
          <span className="truncate">{user.email}</span>
        </div>
        <div className="flex items-center gap-2 font-display text-sm text-ink">
          <HiOutlinePhone className="h-5 w-5 text-gray-500" aria-hidden="true" />
          <span className="truncate">{user.phone || '—'}</span>
        </div>
      </div>
    </div>
  )
}

function Avatar({ user }: { user: UserCard }) {
  const swatch = avatarSwatch(user.id)
  const ringStyle = {
    background:
      'linear-gradient(138deg, rgba(0, 190, 214, 1) 11%, rgba(147, 213, 0, 1) 100%)',
  }
  return (
    <div
      className="flex h-[72px] w-[72px] shrink-0 items-center justify-center rounded-full p-[2px]"
      style={ringStyle}
    >
      <div className="flex h-full w-full items-center justify-center rounded-full bg-white p-[3px]">
        {user.photo_url ? (
          <img
            src={user.photo_url}
            alt={user.name}
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          <div
            className="flex h-full w-full items-center justify-center rounded-full"
            style={{ backgroundColor: swatch.bg }}
          >
            <span
              className="font-display text-2xl font-medium"
              style={{ color: swatch.text }}
            >
              {user.initials}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
